import { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Button } from "@nextui-org/react";
import BlurImage from "../../components/BlurImage";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
};

const stats = [
  { label: "Years Coding", value: 3, suffix: "+" },
  { label: "Projects", value: 10, suffix: "+" },
  { label: "Technologies", value: 24, suffix: "+" },
];

function Counter({ end, suffix, duration = 2000 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const increment = end / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [inView, end, duration]);

  return (
    <span ref={ref}>
      {count}{suffix}
    </span>
  );
}

export default function AboutMe() {
  const handleDownload = () => {
    const pdfPath = "./img/CV. Angelo Julius G. Taruc.pdf";
    const anchorElement = document.createElement("a");
    anchorElement.href = pdfPath;
    anchorElement.download = "CV. Angelo Julius G. Taruc.pdf";
    document.body.appendChild(anchorElement);
    anchorElement.click();
    document.body.removeChild(anchorElement);
  };

  return (
    <section id="AboutMe" className="section-padding bg-white dark:bg-black">
      <div className="section-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div {...fadeUp} className="space-y-8">
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden border-2 border-black/10 dark:border-white/10 shadow-2xl glass">
                <BlurImage
                  src="./img/taruc.png"
                  alt="About Me"
                  className="w-full h-[400px]"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="bento-glass-card text-center py-6"
                >
                  <p className="font-display text-2xl font-bold text-black dark:text-white">
                    <Counter end={stat.value} suffix={stat.suffix} />
                  </p>
                  <p className="font-body text-xs text-black/50 dark:text-white/50 mt-1">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            {...fadeUp}
            transition={{ delay: 0.2 }}
            className="space-y-6"
          >
            <h2 className="section-title">About Me</h2>

            <blockquote className="border-l-4 border-black dark:border-white pl-6 py-2">
              <p className="font-display text-xl italic text-black/60 dark:text-white/60">
                "Turning ideas into functional software solutions — one line of code at a time."
              </p>
            </blockquote>

            <p className="font-body text-base text-black/50 dark:text-white/50 leading-relaxed">
              Hi there! I'm Angelo Julius G. Taruc, a passionate software
              developer with a love for problem-solving and coding. I enjoy
              building scalable and efficient applications that make a positive
              impact.
            </p>
            <p className="font-body text-base text-black/50 dark:text-white/50 leading-relaxed">
              My journey in the world of programming started when I discovered my
              fascination with turning ideas into functional software solutions.
              It wasn't long before I delved into the world of algorithms, data
              structures, and the art of crafting elegant code.
            </p>

            <Button
              onClick={handleDownload}
              size="lg"
              className="font-body font-semibold bg-black text-white dark:bg-white dark:text-black
                         px-8 py-6 rounded-xl hover:bg-black/80 dark:hover:bg-white/80 transition-all duration-200"
            >
              Download CV
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
